/**
 * 관리자 도우미(길잡이) — 운영자가 "어디서 고치지?"를 물으면 **답 + 그 화면으로 데려다준다**.
 *
 * 설계 원칙(중요): 도우미는 **읽고 안내만** 한다. 업로드·게시·삭제는 절대 하지 않는다.
 * 이 서비스의 안전 설계는 일관되게 "AI는 초안, 사람이 승인"이다(NPC 대사·굿즈 문구).
 * 도우미만 예외로 게시 권한을 주면 그 원칙이 무너진다. 그리고 운영자의 진짜 문제는
 * "고치는 게 어려워서"가 아니라 **"어디서 고치는지 몰라서"** 다.
 *
 * 그래서 출력은 글 + `goto`(열어줄 화면) + `highlight`(짚어줄 칸) 뿐이다.
 */
import type { StageRequest } from "./goodsDraft";
import { ADMIN_SCREENS, isAdminScreenId } from "./adminScreens";

export const ASSIST_DAILY_LIMIT = 120; // 운영자 1명이 쓰는 기능. 오작동·비용 폭주만 막는다.
export const ASSIST_MAX_QUESTION = 300;
export const ASSIST_MAX_TURNS = 8; // 최근 대화만 보낸다(비용·지연)

/** 운영자 화면이 지금 어떤 상태인지 — "굿즈 6개 전부 스토어 주소가 비었어요" 같은 답을 위해. */
export interface AssistState {
  screen?: string; // 지금 보고 있는 화면 id
  imagesFilled?: number;
  imagesTotal?: number;
  goodsTotal?: number;
  goodsMissingStore?: number; // 스토어 주소가 없는 상품 수
  noticeCount?: number;
  npcApproved?: number;
  npcPending?: number;
  rallyPoints?: number;
  eventCount?: number;
  hasContactUrl?: boolean;
}

export interface AssistTurn {
  role: "user" | "assistant";
  text: string;
}

export interface AssistAnswer {
  reply: string;
  goto?: string; // 열어줄 화면 id
  highlight?: string; // 짚어줄 슬롯/필드 키
}

// ⚠️ OpenAI 의 구조화 출력(strict)은 **properties 의 모든 키가 required 여야** 한다.
// 그래서 선택 필드를 빼는 대신 **빈 문자열 ""** 을 "없음"으로 쓴다(공급자 중립 — Claude 에서도 동작).
const SCHEMA = {
  type: "object",
  additionalProperties: false,
  required: ["reply", "goto", "highlight"],
  properties: {
    reply: { type: "string", description: "운영자에게 할 말. 3문장 이내, 존댓말, 쉬운 우리말." },
    goto: {
      type: "string",
      description: "열어줄 화면 id. 안내할 화면이 없으면 빈 문자열.",
      enum: [...ADMIN_SCREENS.map((s) => s.id), ""],
    },
    highlight: {
      type: "string",
      description: "그 화면에서 짚어줄 칸의 키(예: world.background.gyeongju). 모르면 빈 문자열.",
    },
  },
} as const;

function screenList(): string {
  return ADMIN_SCREENS.map((s) => `- ${s.id} · ${s.icon} ${s.label}: ${s.desc} (관련어: ${s.find})`).join("\n");
}

function stateLines(st: AssistState): string {
  const L: string[] = [];
  if (st.screen) L.push(`지금 보고 있는 화면: ${st.screen}`);
  if (st.imagesTotal != null) L.push(`그림: ${st.imagesFilled ?? 0}/${st.imagesTotal} 칸 채움`);
  if (st.goodsTotal != null) {
    L.push(`굿즈 ${st.goodsTotal}개` + (st.goodsMissingStore ? ` (그중 ${st.goodsMissingStore}개는 스토어 주소가 비어 있어 [사러 가기]가 동작하지 않음)` : ""));
  }
  if (st.noticeCount != null) L.push(`마을 소식 ${st.noticeCount}개`);
  if (st.npcApproved != null) L.push(`NPC 대사: 승인 ${st.npcApproved}개, 검수 대기 ${st.npcPending ?? 0}개`);
  if (st.rallyPoints != null) L.push(`랠리 지점 ${st.rallyPoints}곳`);
  if (st.eventCount != null) L.push(`행사 ${st.eventCount}개`);
  if (st.hasContactUrl === false) L.push("문의 폼 주소가 비어 있음(커뮤니티에서 '곧 열려요'로 표시)");
  return L.length ? L.join("\n") : "(상태 정보 없음)";
}

const SYSTEM = `너는 그림책 웹서비스 《그레그와 병아리의 경주》의 **관리자 화면 길잡이**다.
쓰는 사람은 개발자가 아닌 출판사 운영자다. 아이들이 보는 서비스를 관리한다.

역할:
- 운영자가 무엇을 고치고 싶은지 듣고, **어느 화면에서 어떻게 하면 되는지** 알려준다.
- 알려줄 화면이 있으면 goto 에 그 화면 id 를 넣는다(운영자가 버튼 한 번으로 이동한다).
- 그 화면 안에서 특정 칸을 짚어줄 수 있으면 highlight 에 키를 넣는다.

반드시 지킬 것:
- **너는 아무것도 고치거나 올리지 못한다.** "제가 바꿔드릴게요"라고 하지 마라.
  대신 "여기서 이렇게 하시면 돼요"라고 안내한다.
- 아래 화면 목록에 **없는 화면을 지어내지 마라**. 없으면 없다고 솔직히 말한다.
- 3문장 이내, 존댓말, 쉬운 우리말. 전문용어(슬롯·매니페스트·API) 대신 "그림 칸", "웹에 올리기"처럼 쓴다.
- 확실하지 않으면 아는 데까지만 말하고 추측을 사실처럼 말하지 않는다.
- 개인정보를 묻거나 만들어 쓰지 않는다.

칸 키(highlight) 규칙 — 아래 형태에 확실히 들어맞을 때만 넣고, 아니면 빈 문자열:
- 존 배경/바닥/벽: world.background.<존> / world.tileset.<존> / world.wall.<존>
  (존 = lobby 마을 · gyeongju 경주 · book 전자책방 · goods 굿즈 · event 행사장)
- 경주 랜드마크를 눌렀을 때 나오는 그림: ar.poster.ar_chumseongdae(첨성대) ·
  ar.poster.ar_seokguram(석굴암) · ar.poster.ar_cheonmachong(천마총) ·
  ar.poster.ar_wolji(동궁과 월지) · ar.poster.ar_sumaksae(수막새) · ar.poster.ar_hwangridan(황리단길)
- 마을 친구 그림: npc.greg_white(그레그) · npc.greg_cat(검은 고양이) · npc.chick(병아리)
- 첫 화면 캐릭터: landing.char.greg · landing.char.chick

이 서비스의 규칙(안내할 때 참고):
- 회원가입·로그인이 없다. 아이 개인정보를 서버에 저장하지 않는다.
- 바꾼 것은 화면 아래 [🚀 웹에 올리기]를 눌러야 아이들에게 보인다.
- NPC 대사는 운영자가 승인한 것만 아이에게 나간다.
- QR 목적지는 우리 사이트 안 주소(/로 시작)만 넣을 수 있다.`;

/** 도우미 한 번 물어보기. */
export function assistRequest(opts: {
  question: string;
  state: AssistState;
  history?: AssistTurn[];
}): StageRequest {
  const q = opts.question.trim().slice(0, ASSIST_MAX_QUESTION);
  const hist = (opts.history ?? []).slice(-ASSIST_MAX_TURNS);
  const parts = [
    `[관리자 화면 목록]\n${screenList()}`,
    `[지금 상태]\n${stateLines(opts.state)}`,
    hist.length
      ? `[앞선 대화]\n${hist.map((t) => `${t.role === "user" ? "운영자" : "도우미"}: ${t.text}`).join("\n")}`
      : "",
    `[운영자 질문]\n${q}`,
  ].filter(Boolean);
  return {
    system: SYSTEM,
    userText: parts.join("\n\n"),
    schema: SCHEMA as unknown as Record<string, unknown>,
    maxTokens: 700,
  };
}

const KEY_RE = /^[a-zA-Z0-9._-]{1,64}$/;

/** LLM 출력도 외부 입력이다(불변식 7) — 없는 화면으로 보내거나 이상한 키를 짚지 않게. */
export function sanitizeAssistAnswer(raw: unknown): AssistAnswer {
  const o = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const reply =
    typeof o.reply === "string" && o.reply.trim()
      ? o.reply.trim().slice(0, 600)
      : "잘 모르겠어요. 다른 말로 물어봐 주시겠어요?";
  const goto = isAdminScreenId(o.goto) ? (o.goto as string) : undefined;
  const highlight = typeof o.highlight === "string" && KEY_RE.test(o.highlight) ? o.highlight : undefined;
  return { reply, ...(goto ? { goto } : {}), ...(highlight ? { highlight } : {}) };
}
